// src/pages/ResetPassword.jsx — FINAL
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { api } from '../lib/api.js';
import { BOOKING_BASE } from '../constants.js';

import Button from '../components/Button.jsx';
import Input from '../components/Input.jsx';
import LogoMark from '../components/LogoMark.jsx';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function ResetPassword() {
  const navigate = useNavigate();
  const query = useQuery();

  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const t = (query.get('token') || '').trim();
    if (t) setToken(t);
  }, [query]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const validate = () => {
    const ne = {};
    if (!token.trim()) ne.token = 'Reset token is required';
    if (!form.password) ne.password = 'Password is required';
    if (form.password && form.password.length < 6) ne.password = 'Password must be at least 6 characters';
    if (form.password !== form.confirmPassword) ne.confirmPassword = 'Passwords do not match';
    setErrors(ne);
    return Object.keys(ne).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        token: token.trim(),
        password: form.password,
        confirm: form.confirmPassword,
      });

      toast.success('Password updated. Please sign in.');
      navigate(`${BOOKING_BASE}/login`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="w-full max-w-md"
      >
        {/* Brand */}
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 inline-flex items-center justify-center">
            <LogoMark className="w-20 h-20 drop-shadow-[0_6px_20px_rgba(59,130,246,0.35)]" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Spacebook</h1>
          <p className="text-gray-300">Reset your password</p>
        </div>

        {/* Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25 }}
          className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 shadow-2xl"
        >
          <form onSubmit={submit} className="space-y-4">
            <Input
              name="token"
              label="Reset Token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              error={errors.token}
              placeholder="Paste your reset token"
            />

            <Input
              name="password"
              label="New Password"
              type="password"
              value={form.password}
              onChange={onChange}
              error={errors.password}
              placeholder="Enter new password"
            />

            <Input
              name="confirmPassword"
              label="Confirm New Password"
              type="password"
              value={form.confirmPassword}
              onChange={onChange}
              error={errors.confirmPassword}
              placeholder="Confirm new password"
            />

            <Button type="submit" loading={loading} className="w-full mt-2" size="lg">
              Update Password
            </Button>

            <div className="pt-2 text-center">
              <button
                type="button"
                onClick={() => navigate(`${BOOKING_BASE}/login`)}
                className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
              >
                Back to Sign In
              </button>
            </div>
          </form>
        </motion.div>

        <div className="mt-6 text-center text-sm text-gray-400">
          © {new Date().getFullYear()} <span className="font-semibold text-white">Spacebook</span> — Developed by IT Team.
        </div>
      </motion.div>
    </div>
  );
}

export default ResetPassword;
